import { format } from "date-fns";

type TypeCombineDateTimeConfig = {
  hideSecond: boolean;
  hideMillisecond: boolean;
};

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function ToIsoUtcDateTime(date?: Date): string {
  return date ? new Date(date).toISOString() : new Date().toISOString();
}

function ToIsoUtcDate(date?: Date): string {
  const dateObj = date ? new Date(date) : new Date();
  dateObj.setUTCHours(0, 0, 0, 0);
  return dateObj.toISOString();
}

function isDateAfterToday(date?: Date) {
  return (
    !!date &&
    new Date(date.toDateString()) > new Date(new Date().toDateString())
  );
}

function isDateBeforeToday(date?: Date) {
  return (
    !!date &&
    new Date(date.toDateString()) < new Date(new Date().toDateString())
  );
}

function addTimeToDate(time: string, dateToAdd?: Date) {
  const date = dateToAdd || new Date();
  const [hour, minute, second, millisecond] = time?.split(":");
  hour && date.setHours(Number(hour));
  minute && date.setMinutes(Number(minute));
  second && date.setSeconds(Number(second));
  millisecond && date.setMilliseconds(Number(millisecond));
  return date;
}

function combineDateTime(
  date?: Date,
  time?: Date,
  config?: TypeCombineDateTimeConfig
) {
  if (!date || !time) return new Date();

  const dateTime = new Date(date.getTime());
  dateTime.setHours(time.getHours());
  dateTime.setMinutes(time.getMinutes());
  dateTime.setSeconds(config?.hideSecond ? 0 : time.getSeconds());
  dateTime.setMilliseconds(
    config?.hideMillisecond ? 0 : time.getMilliseconds()
  );
  return dateTime;
}

function formatDate(date?: Date | string) {
  return (
    new Date(date || new Date()).getMonth() +
    1 +
    "-" +
    new Date(date || new Date()).getDate() +
    "-" +
    new Date(date || new Date()).getFullYear()
  );
}

function formatTime(date?: Date | string) {
  const currDate = new Date(date || new Date());

  var hours = currDate.getHours();
  var minutes: string | number = currDate.getMinutes();
  var ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours = hours ? hours : 12;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  var strTime = hours + ":" + minutes + ampm;
  return strTime;
}

function minuteToHourString(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const minutesLeft = Math.floor(minutes % 60);
  const seconds = Math.floor((minutes % 1) * 60);
  const hoursString = hours > 0 ? `${hours} hour(s) ` : "";
  const minutesString = minutesLeft > 0 ? `${minutesLeft} minute(s) ` : "";
  const secondsString = seconds > 0 ? `${seconds} second(s)` : "";
  return `${hoursString}${minutesString}${secondsString}`;
}

function getDateOnly(date: string) {
  const dateObj = new Date(date);
  return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
}

function getStartOfMonth() {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getEndOfMonth() {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getPreviousInfo() {
  const currentDate = new Date();
  const previousMonth =
    currentDate.getMonth() === 0 ? 11 : currentDate.getMonth() - 1;
  const previousMonthYear =
    currentDate.getMonth() === 0
      ? currentDate.getFullYear() - 1
      : currentDate.getFullYear();
  const previousDate = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate() - 1
  );

  return { previousDate, previousMonthYear, previousMonth };
}

function getStartOfPreviousMonth() {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth() - 1, 1);
}

function getMaxPossibleHour(date: Date) {
  date.setHours(23, 59, 59, 0);
  return date;
}

function getMinimumPossibleHour(date: Date) {
  date.setHours(0, 0, 0, 0);
  return date;
}

function getEndOfPreviousMonth() {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 0, 23, 59, 59);
}

function getDateAfterThirtyDays(givenDate: string) {
  const date = new Date(givenDate);
  date.setDate(date.getDate() + 30);
  return date;
}

function getDayMonth(isoDate: string) {
  const date = new Date(isoDate);
  const day = date.getUTCDate();
  const month = monthNames[date.getUTCMonth()];
  return `${day} ${month}`;
}

function getDayMonthV1(isoDate: string) {
  const date = new Date(isoDate);
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  return `${day} ${month}`;
}

function isValidGap(startDate: Date, endDate: Date, maxDays = 30) {
  const diffInMilliseconds = Math.abs(endDate.getTime() - startDate.getTime());
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
  return diffInDays <= maxDays;
}

function formatHumanFriendlyDate(date?: Date | string) {
  const dateObj = new Date(date || new Date());
  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();
  return `${month}-${day}-${year}`;
}

function formatLocalDateOnly(date?: Date | string) {
  // Converts UTC date to local date and formats it for display
  const dateObj = date ? new Date(date) : new Date();
  const localDate = new Date(
    dateObj.getUTCFullYear(),
    dateObj.getUTCMonth(),
    dateObj.getUTCDate()
  );
  return localDate.toLocaleDateString(undefined, {
    year: '2-digit',
    month: 'short',
    day: 'numeric'
  });
}

function formatDateWithoutTimezoneMinusOne(dateString: string): string {
  if (!dateString) return '';
  // Assumes dateString is 'YYYY-MM-DD'
  const localDate = new Date(
    dateString
  );
  if (localDate.getDate() === 1) {
    // Subtract one day if it's the first day of the month
    localDate.setDate(localDate.getDate() - 1);
  }
  return format(localDate, 'MMM-dd-yy');
}

export const UtilityDate = {
  ToIsoUtcDateTime,
  ToIsoUtcDate,
  isDateAfterToday,
  isDateBeforeToday,
  formatLocalDateOnly,
  minuteToHourString,
  combineDateTime,
  formatDate,
  formatTime,
  getDateOnly,
  getStartOfMonth,
  getEndOfMonth,
  getStartOfPreviousMonth,
  getEndOfPreviousMonth,
  getMaxPossibleHour,
  getMinimumPossibleHour,
  getDateAfterThirtyDays,
  addTimeToDate,
  getDayMonth,
  getDayMonthV1,
  getPreviousInfo,
  isValidGap,
  formatDateWithoutTimezoneMinusOne,
  formatHumanFriendlyDate, // Add the new function to exports
  monthNames, // Export monthNames
};
